import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';

/**
 * EntityModalContext
 * 
 * Provides a unified way to open entity detail modals from anywhere in the app.
 * EntityLinkedText and other components can use this without prop drilling.
 * 
 * Parent modals register refresh callbacks via registerParentCallbacks().
 * When a child modal is opened via a co-pilot link, EntityModals passes these
 * callbacks as parentRefresh so the child can refresh the parent's local state
 * after saving, instead of triggering a full page refresh.
 */

const EntityModalContext = createContext(null);

export const useEntityModal = () => {
  const context = useContext(EntityModalContext);
  if (!context) {
    return {
      openEntityModal: () => {
        console.warn('EntityModalContext not available');
      },
      selectedEntity: null,
      closeEntityModal: () => {},
      registerParentCallbacks: () => {},
      unregisterParentCallbacks: () => {},
      parentCallbacksRef: { current: null },
    };
  }
  return context;
};

/**
 * EntityModalProvider
 * 
 * Wrap your page component with this to enable entity linking.
 * Pass in the data sources and modal components.
 */
export const EntityModalProvider = ({ 
  children,
  // Data sources for looking up entities
  features = [],
  epics = [],
  personas = [],
  goals = [],
  initiatives = [],
  requests = [],
  products = [],  // For segment lookup (segments are embedded in products)
  // Optional: custom handlers for entity types that don't have modals
  onNavigateToTab,
}) => {
  // Currently selected entity for modal (includes zIndex for stacking)
  const [selectedEntity, setSelectedEntity] = useState(null);
  
  /**
   * Find a segment by ID across all products
   * Segments are embedded in products.market_segments[]
   */
  const findSegmentById = useCallback((segmentId) => {
    for (const product of products) {
      const segment = product.market_segments?.find(s => s.segment_id === segmentId);
      if (segment) {
        return {
          ...segment,
          product_id: product.product_id,
          product_name: product.name,
        };
      }
    }
    return null;
  }, [products]);
  
  const openEntityModal = useCallback((entityType, entityId, options = {}) => {
    const { zIndex = 50 } = options;
    
    try {
      let entity = null;
      
      switch (entityType) {
        case 'feature':
          entity = features.find(f => f.feature_id === entityId);
          if (entity) {
            setSelectedEntity({ type: 'feature', data: entity, zIndex });
          } else {
            toast.error('Feature not found - it may have been deleted');
          }
          break;
          
        case 'epic':
          entity = epics.find(e => e.epic_id === entityId);
          if (entity) {
            setSelectedEntity({ type: 'epic', data: entity, zIndex });
          } else {
            toast.error('Epic not found - it may have been deleted');
          }
          break;
          
        case 'persona':
          entity = personas.find(p => p.persona_id === entityId);
          if (entity) {
            setSelectedEntity({ type: 'persona', data: entity, zIndex });
          } else {
            toast.error('Persona not found - it may have been deleted');
          }
          break;
          
        case 'goal':
          entity = goals.find(g => g.goal_id === entityId);
          if (entity) {
            // Support tab and kpiId options for deep-linking to KPI tab
            setSelectedEntity({ 
              type: 'goal', 
              data: entity, 
              zIndex,
              tab: options.tab,      // e.g., 'kpis' to open KPI tab
              kpiId: options.kpiId,  // e.g., specific KPI to highlight
            });
          } else {
            toast.error('Goal not found - it may have been deleted');
          }
          break;
          
        case 'request':
          entity = requests.find(r => r.request_id === entityId);
          if (entity) {
            setSelectedEntity({ type: 'request', data: entity, zIndex });
          } else {
            toast.error('Request not found - it may have been deleted');
          }
          break;
          
        case 'initiative':
          entity = initiatives.find(i => i.initiative_id === entityId);
          if (entity) {
            setSelectedEntity({ type: 'initiative', data: entity, zIndex });
          } else {
            toast.error('Initiative not found - it may have been deleted');
          }
          break;
          
        case 'segment':
          entity = findSegmentById(entityId);
          if (entity) {
            setSelectedEntity({ type: 'segment', data: entity, zIndex });
          } else {
            toast.error('Segment not found - it may have been deleted');
          }
          break;
          
        case 'release':
          toast.info('Navigate to Releases tab to view this release');
          onNavigateToTab?.('releases');
          break;
          
        case 'report':
          toast.info('Navigate to Research tab to view this report');
          onNavigateToTab?.('research');
          break;
          
        default:
          toast.info(`View ${entityType} in its respective tab`);
      }
    } catch (err) {
      console.error('Error opening entity modal:', err);
      toast.error('Could not open item - it may have been deleted');
    }
  }, [features, epics, personas, goals, initiatives, requests, findSegmentById, onNavigateToTab]);
  
  const closeEntityModal = useCallback(() => {
    setSelectedEntity(null);
  }, []);
  
  // Parent modal callback registration — allows child modals opened via
  // co-pilot links to refresh the parent's local state instead of doing
  // a full page refresh. Keys are entity types the parent handles.
  // e.g. { epic: loadLinkedEpics, feature: loadTaggedFeatures, markDirty: fn }
  const parentCallbacksRef = useRef(null);
  
  const registerParentCallbacks = useCallback((callbacks) => {
    parentCallbacksRef.current = callbacks;
  }, []);
  
  const unregisterParentCallbacks = useCallback(() => {
    parentCallbacksRef.current = null;
  }, []);
  
  const value = {
    openEntityModal,
    selectedEntity,
    closeEntityModal,
    registerParentCallbacks,
    unregisterParentCallbacks,
    parentCallbacksRef,
  };
  
  return (
    <EntityModalContext.Provider value={value}>
      {children}
    </EntityModalContext.Provider>
  );
};

export default EntityModalContext;
